import { useCallback, useMemo, useState } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import withFormStore, { useForm } from "../Form/withFormStore";
import Modal from "../Modals/Modal";
import EmailField from "../Form/Field/EmailField";
import Button from "../Buttons/Button";
import { useFormatMessage } from "../Intl/Context";
import { intlMessages as formPageIntlMessages } from "../ParticipativeModule/FormPage";

import { sendPasswordReset } from "../../mutations/SendPasswordResetMutation";

const AuthenticationPasswordForm = props => {
  const { onClose, email } = props;
  const { formState, validateForm } = useForm();

  const t = useFormatMessage();
  const labels = useMemo(
    () => ({
      title: t(intlMessages.title),
      description: t(intlMessages.description),
      requiredFieldsHint: t(formPageIntlMessages.requiredFieldsHint),
      email: t(intlMessages.email),
      submit: t(intlMessages.submit),
      successMessage: t(intlMessages.successMessage),
      cancel: t(intlMessages.cancel)
    }),
    [t]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const formClassName = classNames({
    "authentication-fieldset": true,
    "authentication-sign-in": true,
    box: true,
    "margin-bottom-xs": true,
    "padding-x-lg": true,
    "padding-y-md": true,
    loading: isSubmitting
  });

  const breadcrumbs = [{ title: labels.title }];

  const handleFormResponse = useCallback(() => {
    setIsSubmitting(false);

    onClose(true, labels.successMessage);
  }, [labels.successMessage, onClose]);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      // Do not submit form if it is already submitting
      if (isSubmitting) return;

      if (validateForm()) {
        setIsSubmitting(true);

        sendPasswordReset(formState, handleFormResponse);
      }
    },
    [formState, handleFormResponse, isSubmitting, validateForm]
  );

  return (
    <Modal
      isOpen={true}
      shouldCloseOnOverlayClick={true}
      onRequestClose={onClose}
      breadcrumbs={breadcrumbs}
      title={labels.title}
      className="modal-sm"
    >
      <form className={formClassName} onSubmit={handleSubmit}>
        <p className="help-block">{labels.description}</p>

        <p
          className="form-hint-required-fields margin-bottom-sm text-small"
          dangerouslySetInnerHTML={{ __html: labels.requiredFieldsHint }}
        />

        <EmailField
          fieldName="email"
          label={labels.email}
          required
          defaultValue={email}
          autoComplete="email"
        />

        <Button
          type="submit"
          className="btn btn-primary btn-block-md box-shadow-lg"
          onClick={handleSubmit}
          disabled={isSubmitting}
          data-loading={isSubmitting}
          label={labels.submit}
          iconClassName="icon-arrow-right"
          iconPosition="after"
        />

        <Button
          type="button"
          className="btn btn-primary-inverse btn-block-md box-shadow-lg"
          onClick={onClose}
          disabled={isSubmitting}
          data-loading={isSubmitting}
          label={labels.cancel}
          iconClassName="icon-arrow-right"
          iconPosition="after"
        />
      </form>
    </Modal>
  );
};

export default withFormStore(AuthenticationPasswordForm);

const intlMessages = defineMessages({
  title: {
    id: "authentication.passwordForm.title",
    defaultMessage: "Réinitialisation du mot de passe"
  },
  description: {
    id: "authentication.passwordForm.description",
    defaultMessage: `
      Un e-mail vous sera envoyé avec un lien vous permettant de définir
      un nouveau de mot de passe de connexion à votre compte.
    `
  },
  email: {
    id: "authentication.passwordForm.labels.email",
    defaultMessage: "E-mail"
  },
  submit: {
    id: "authentication.passwordForm.submit",
    defaultMessage: "Envoyer le mail"
  },
  successMessage: {
    id: "authentication.passwordForm.successMessage",
    defaultMessage: `
      Si l'adresse e-mail renseignée est bien associée à un compte existant, un e-mail
      a été envoyé avec les instructions de réinitialisation de votre mot de passe.
    `
  },
  cancel: {
    id: "authentication.passwordForm.cancel",
    defaultMessage: "Annuler"
  }
});
